import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import type {
  DialogProps,
  DialogIconProps,
  DialogHeaderProps,
  DialogContentProps,
  DialogListProps,
  DialogListItemProps,
  DialogFooterProps,
  DialogActionProps,
} from './types';
import { useDialog } from './useDialog';

// ============================================================================
// Default Icon
// ============================================================================

const DefaultIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </svg>
);

// ============================================================================
// Dialog Container Component
// ============================================================================

const Dialog: React.FC<DialogProps & { 'data-testid'?: string }> = ({
  open,
  onClose,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  maxWidth = 312,
  className = '',
  children,
  'data-testid': dataTestId,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { handleOverlayClick } = useDialog({
    open,
    onClose,
    closeOnOverlayClick,
    closeOnEscape,
    dialogRef,
  });

  if (!open) {
    return null;
  }

  const dialogContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ fontFamily: 'Roboto, sans-serif' }}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        data-testid={dataTestId}
        className={`
          relative
          bg-neutral-100
          rounded-xl
          shadow-xl
          overflow-hidden
          animate-in
          fade-in
          zoom-in-95
          duration-200
          ${className}
        `}
        style={{
          maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
          width: '100%',
          margin: '24px',
        }}
      >
        {children}
      </div>
    </div>
  );

  // Render using portal to document body
  return createPortal(dialogContent, document.body);
};

// ============================================================================
// Dialog Icon Component
// ============================================================================

const DialogIcon: React.FC<DialogIconProps> = ({
  icon,
  bgColor = 'bg-warning-95',
  iconColor = 'text-warning-40',
  className = '',
}) => {
  return (
    <div className={`flex justify-center pt-6 pb-2 ${className}`}>
      <div
        className={`
          w-12 h-12 rounded-full
          flex items-center justify-center
          ${bgColor}
        `}
      >
        {icon || <DefaultIcon className={`w-6 h-6 ${iconColor}`} />}
      </div>
    </div>
  );
};

// ============================================================================
// Dialog Header Component
// ============================================================================

const DialogHeader: React.FC<DialogHeaderProps> = ({
  title,
  className = '',
}) => {
  return (
    <div className={`px-6 pt-6 pb-2 ${className}`}>
      <h2
        className="text-neutral-10 font-medium text-center"
        style={{
          fontSize: 24,
          lineHeight: '32px',
        }}
      >
        {title}
      </h2>
    </div>
  );
};

// ============================================================================
// Dialog Content Component
// ============================================================================

const DialogContent: React.FC<DialogContentProps> = ({
  children,
  className = '',
}) => {
  return (
    <div className={`px-6 py-2 ${className}`}>
      <p
        className="text-neutral-40 text-center"
        style={{
          fontSize: 14,
          lineHeight: '20px',
        }}
      >
        {children}
      </p>
    </div>
  );
};

// ============================================================================
// Dialog List Component
// ============================================================================

const DialogList: React.FC<DialogListProps> = ({
  scrollable = false,
  maxHeight = 200,
  className = '',
  children,
}) => {
  return (
    <div
      className={`py-2 ${scrollable ? 'overflow-y-auto' : ''} ${className}`}
      style={scrollable ? { maxHeight } : undefined}
    >
      {children}
    </div>
  );
};

// ============================================================================
// Dialog List Item Component
// ============================================================================

const DialogListItem: React.FC<DialogListItemProps> = ({
  avatar,
  avatarText = 'A',
  label,
  trailingText,
  hasCheckbox = false,
  checked = false,
  onChange,
  className = '',
}) => {
  const handleClick = () => {
    if (hasCheckbox && onChange) {
      onChange(!checked);
    }
  };

  return (
    <div
      className={`
        flex items-center gap-4 px-6 py-3
        hover:bg-neutral-95 transition-colors cursor-pointer
        ${className}
      `}
      onClick={handleClick}
      role={hasCheckbox ? 'checkbox' : 'listitem'}
      aria-checked={hasCheckbox ? checked : undefined}
    >
      {/* Avatar */}
      <div className="shrink-0">
        {avatar || (
          <div
            className="w-10 h-10 rounded-full bg-primary-color-90 text-primary-color-40 flex items-center justify-center font-medium"
            style={{ fontSize: 16 }}
          >
            {avatarText}
          </div>
        )}
      </div>

      {/* Label */}
      <div className="flex-1 min-w-0">
        <p
          className="text-neutral-10 truncate"
          style={{
            fontSize: 16,
            lineHeight: '24px',
          }}
        >
          {label}
        </p>
      </div>

      {/* Trailing text */}
      {trailingText && (
        <span
          className="text-neutral-40 shrink-0"
          style={{
            fontSize: 14,
            lineHeight: '20px',
          }}
        >
          {trailingText}
        </span>
      )}

      {/* Checkbox */}
      {hasCheckbox && (
        <div className="shrink-0">
          <div
            className={`
              w-5 h-5 rounded
              border-2 transition-colors
              flex items-center justify-center
              ${
                checked
                  ? 'bg-primary-color-40 border-primary-color-40'
                  : 'bg-transparent border-neutral-60'
              }
            `}
          >
            {checked && (
              <svg
                className="w-3 h-3 text-white"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="2,6 5,9 10,3" />
              </svg>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Dialog Footer Component
// ============================================================================

const DialogFooter: React.FC<DialogFooterProps> = ({
  className = '',
  children,
}) => {
  return (
    <div
      className={`flex items-center justify-end gap-2 px-6 py-4 ${className}`}
    >
      {children}
    </div>
  );
};

// ============================================================================
// Dialog Action Component
// ============================================================================

const DialogAction: React.FC<DialogActionProps> = ({
  label,
  variant = 'secondary',
  onClick,
  disabled = false,
  className = '',
}) => {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`
        px-4 py-2 rounded-full font-medium
        transition-all duration-200
        ${
          variant === 'primary'
            ? 'bg-primary-color-40 text-neutral-100 hover:bg-primary-color-50'
            : 'bg-transparent text-primary-color-40 hover:bg-primary-color-95'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      style={{
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 500,
      }}
    >
      {label}
    </button>
  );
};

// ============================================================================
// Exports
// ============================================================================

export {
  Dialog,
  DialogIcon,
  DialogHeader,
  DialogContent,
  DialogList,
  DialogListItem,
  DialogFooter,
  DialogAction,
};

export default Dialog;
